import React, { forwardRef, useRef, useState } from "react";
import NextImage from "next/image";
import { GoSync } from "react-icons/go";
import { IconType } from "react-icons";
import config from "src/config";
import { s3Upload } from "src/libs/s3-lib";
import { useAuth } from "src/hooks/useAuth";
import {
  Button,
  type ButtonSize,
  type ButtonVariant,
  type ButtonWidth,
} from "@memento/ui/Button";
import { toastError } from "src/libs/toast-lib";
import { cn } from "src/utils/cn";
import { megabytesToBytes } from "src/libs/units-lib";

const fileTypeToMimeStartsWith: Record<
  FileUploadButtonProps["fileType"],
  string
> = {
  image: "image/",
};

async function getImageSize(file: File) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (e) {
      const img = new Image();

      img.onload = function () {
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = function (err) {
        reject(err);
      };

      if (!e.target) {
        return reject(new Error("No target."));
      }

      if (typeof e.target.result !== "string") {
        return reject(new Error("No target result."));
      }

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  });
}

type FileType = "image";

const fileTypeToHuman: Record<FileType, string> = {
  image: "image",
};

export type OnUploadPhoto = ({
  url,
  width,
  height,
}: {
  url: string;
  /** Will provide width and height if we can determine it in the browser. */
  width?: number;
  /** Will provide width and height if we can determine it in the browser. */
  height?: number;
}) => void;

type FileUploadButtonProps = {
  disabled?: boolean;
  iconSrc?: string;
  maxUploadSizeMb?: number;
  fileType: FileType;
  // show a preview of the file
  preview?: {
    src: string;
    alt?: string;
    // How to show the preview. This lets you e.g. show the preview as though
    // it's disabled, even if the FileUploadButton button itself is enabled or
    // vice versa. THe default will match the `disabled` prop of
    // FileUploadButton.
    visualState?: "enabled" | "disabled";
  };
  onUploadStart?: (selectedFile: File) => void;
  onUploadSuccess?: OnUploadPhoto;
  onUploadFailed?: () => void;
  // called on failure or success
  onUploadFinally?: () => void;
  // if true, the button will visually be in the same state it's in when
  // uploading
  isLoading?: boolean;
  text: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  width?: ButtonWidth;
  className?: string;
  IconComponent?: IconType;
};

export const FileUploadButton = forwardRef(
  (
    {
      disabled = false,
      isLoading = false,
      onUploadStart = () => {},
      onUploadSuccess = () => {},
      onUploadFailed = () => {},
      onUploadFinally = () => {},
      fileType,
      iconSrc,
      preview,
      maxUploadSizeMb = config.MAX_ATTACHMENT_SIZE.mb,
      text,
      variant,
      size,
      width,
      className,
      IconComponent,
    }: FileUploadButtonProps,
    ref
  ) => {
    const { getAccessToken } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const maxUploadSizeBytes = megabytesToBytes(maxUploadSizeMb);

    const isBusy = isLoading || isUploading;

    const previewVisualState = preview?.visualState
      ? preview.visualState
      : disabled
        ? "disabled"
        : "enabled";

    // this only uploads the first file.
    const uploadFile = async (files: FileList) => {
      if (disabled) {
        return;
      }

      if (isBusy) {
        return;
      }

      const file = files[0];

      if (!file) {
        onUploadFailed();
        return;
      }

      if (file.size > maxUploadSizeBytes) {
        toastError(`Please pick a file smaller than ${maxUploadSizeMb} MB.`);
        onUploadFailed();
        return;
      }

      if (!file.type.startsWith(fileTypeToMimeStartsWith[fileType])) {
        toastError(`Please pick an ${fileTypeToHuman[fileType]} file.`);
        onUploadFailed();
        return;
      }

      try {
        setIsUploading(true);
        onUploadStart(file);

        let imageSize: { width: number; height: number } | null = null;

        try {
          imageSize = fileType === "image" ? await getImageSize(file) : null;
        } catch {
          // do nothing, this is just an unsupported image type (e.g. HEIC)
        }

        const accessToken = await getAccessToken();
        const uploaded = await s3Upload(file, accessToken);

        if (fileType === "image") {
          onUploadSuccess({ url: uploaded, ...(imageSize ? imageSize : {}) });
        }
      } catch (e) {
        console.error(`Error uploading ${fileType}`, e);
        toastError(
          `Something went wrong and the ${fileTypeToHuman[fileType]} could not be uploaded.`
        );
        onUploadFailed();
      } finally {
        setIsUploading(false);
        onUploadFinally();
        resetFileInput();
      }
    };

    const resetFileInput = () => {
      if (fileRef.current) {
        fileRef.current.value = "";
      }
    };

    const handleAttachmentChange = async (e: { target: any; type?: any }) => {
      if (e.target?.files?.length > 0) {
        await uploadFile(e.target.files);
      }
    };

    const handleClick = (e: { preventDefault: () => void }) => {
      e.preventDefault();
      fileRef.current?.click();
    };

    return (
      <div className="flex flex-col gap-4">
        {preview ? (
          <div
            className={cn(
              "relative flex items-center justify-center transition-all"
            )}
          >
            <img
              src={preview.src}
              alt={preview.alt ?? ""}
              className={cn(
                "h-40 object-contain transition-all",
                previewVisualState === "disabled" || isBusy
                  ? "opacity-50 grayscale"
                  : "",
                isBusy ? "cursor-wait" : ""
              )}
            />
          </div>
        ) : null}
        <input
          ref={fileRef}
          onChange={handleAttachmentChange}
          type="file"
          accept={`${fileTypeToMimeStartsWith[fileType]}*`}
          className="hidden"
        />
        <Button
          ref={ref}
          type="button"
          variant={variant}
          size={size}
          width={width}
          className={cn(className, "flex items-center justify-center gap-2")}
          onClick={handleClick}
          disabled={disabled || isBusy}
        >
          {isBusy ? (
            <GoSync
              className="animate-[spin_3s_reverse_linear_infinite] bg-transparent"
              size="24"
            />
          ) : (
            <>
              {iconSrc ? (
                <NextImage src={iconSrc} alt="" width="16" height="16" />
              ) : null}
              {IconComponent ? <IconComponent /> : null}
              {text}
            </>
          )}
        </Button>
      </div>
    );
  }
);
